import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Heart, ChatCircle, Tag, PushPin, Camera, X, Flag, DotsThree,
  TrendUp, Clock, Bell, PaperPlaneTilt, Eye, EyeSlash, Sparkle,
  ArrowLeft, Users, Coins, Diamond, BookOpen, ShieldCheck, Hourglass,
} from "@phosphor-icons/react";

const GROUP_ICONS = { Coins, Diamond, Heart, BookOpen };
function fmtMembers(n) { return Number(n || 0).toLocaleString("en-IN"); }

const TAG_PALETTE = ["#fb923c", "#a78bfa", "#22d3ee", "#fb7185", "#34d399", "#facc15"];
function tagColor(t) {
  let h = 0; for (let i = 0; i < (t || "").length; i++) h = (h * 31 + t.charCodeAt(i)) & 0xfff;
  return TAG_PALETTE[h % TAG_PALETTE.length];
}

export default function ForumPage() {
  const [searchParams] = useSearchParams();
  const groupSlug = searchParams.get("group") || null;
  const [me, setMe] = useState(null);
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [sort, setSort] = useState("recent");
  const [tag, setTag] = useState(null);
  const [tags, setTags] = useState([]);
  const [compose, setCompose] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifsOpen, setNotifsOpen] = useState(false);

  const load = useCallback(async (resetSkip = true) => {
    const s = resetSkip ? 0 : skip;
    try {
      const params = { sort, tag, limit: 20, skip: s };
      if (groupSlug) params.group = groupSlug;
      const { data } = await api.get("/forum/posts", { params });
      setPosts((prev) => (resetSkip ? data.items : [...prev, ...data.items]));
      setHasMore(data.has_more);
      setSkip(s + data.items.length);
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Please sign in to use the Community.");
      else toast.error("Could not load posts.");
    }
  }, [sort, tag, skip, groupSlug]);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/forum/tags/popular").then(({ data }) => setTags(data.items)).catch(() => {});
    api.get("/forum/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, []);

  // Fetch group metadata when scoped to one. Using `groupSlug` as part of the
  // group state shape lets us detect mismatched cached data without a
  // synchronous reset (which trips the react-hooks lint rule).
  useEffect(() => {
    if (!groupSlug) return;
    let cancelled = false;
    api.get(`/forum/groups/${groupSlug}`)
      .then(({ data }) => { if (!cancelled) setGroup({ ...data, _slug: groupSlug }); })
      .catch(() => { if (!cancelled) setGroup({ _missing: true, _slug: groupSlug }); });
    return () => { cancelled = true; };
  }, [groupSlug]);

  // Treat stale cached group as null until the new fetch lands.
  const effectiveGroup = !groupSlug ? null : (group && group._slug === groupSlug ? group : null);
  const groupMissing = !!(effectiveGroup && effectiveGroup._missing);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(true); }, [sort, tag, groupSlug]);

  async function handleJoinGroup() {
    if (groupSlug) {
      try {
        await api.post(`/forum/groups/${groupSlug}/join`);
        const { data } = await api.get(`/forum/groups/${groupSlug}`);
        setGroup({ ...data, _slug: groupSlug });
        toast.success("Joined! You can now post in this group.");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't join.");
      }
    }
  }

  if (me === null) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
        <Header />
        <main className="flex-1 max-w-2xl mx-auto px-4 py-16 text-center">
          <Sparkle size={48} weight="duotone" className="mx-auto text-gold mb-4" />
          <h1 className="font-serif-display text-3xl">Sign in to join the Astrology Community Group</h1>
          <p className="text-muted mt-3">Our community is private to signed-in seekers.</p>
          <Link to="/login" className="inline-block mt-6 btn-primary rounded-full px-6 py-2.5">Sign in</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (groupMissing) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
        <Header />
        <main className="flex-1 max-w-xl mx-auto px-4 py-16 text-center">
          <h1 className="font-serif-display text-2xl">Group not found</h1>
          <Link to="/community" className="mt-4 inline-block text-sm text-gold hover:underline">← Back to community</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="forum-page">
      <Header />
      {/* Group cover banner */}
      {effectiveGroup && !groupMissing && <GroupCoverBanner group={effectiveGroup} onJoin={handleJoinGroup} />}

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-6">
        {!effectiveGroup && (
          /* Hero (only on global feed) */
          <section className="mb-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gold flex items-center gap-1.5">
                  <Sparkle size={12} weight="duotone" /> Astrology Community Group
                </div>
                <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light">Welcome, {me.name?.split(" ")[0]}</h1>
                <Link to="/community" className="text-xs text-gold hover:underline mt-2 inline-block">Browse all groups →</Link>
              </div>
              <button onClick={() => setNotifsOpen(true)} className="relative p-2 rounded-full hover:bg-white/60" data-testid="forum-notifs-btn" aria-label="Notifications">
                <Bell size={22} weight="duotone" />
                {unread > 0 && <span className="absolute -top-0.5 -right-0.5 text-[10px] font-bold bg-rose-500 text-white rounded-full px-1.5">{unread}</span>}
              </button>
            </div>
            {posts.length === 0 && (
              <div className="mt-4 rounded-2xl p-5 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200" data-testid="forum-welcome-banner">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 mb-1">New here? Try one of these</div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {["Share your big Vedic win!", "Ask a question to the community", "Post your kundali insight"].map((p) => (
                    <button key={p} onClick={() => setCompose({ title: p, body: "" })} className="px-3 py-1.5 rounded-full bg-amber-100 hover:bg-amber-200 text-xs text-amber-900 font-medium">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Compose CTA + sort */}
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          {effectiveGroup && !effectiveGroup.is_member ? (
            <div className="flex-1 min-w-0 text-left px-4 py-3 rounded-full bg-white border border-soft text-muted">
              <span className="font-light text-sm">Join this group to post or comment.</span>
            </div>
          ) : (
            <button onClick={() => setCompose({})} className="flex-1 min-w-0 text-left px-4 py-3 rounded-full bg-white border border-soft text-muted hover:border-gold" data-testid="open-compose">
              <span className="font-light">{effectiveGroup ? `Share something with ${effectiveGroup.name}…` : "Share something with the community…"}</span>
            </button>
          )}
          <div className="flex gap-1 bg-white border border-soft rounded-full p-1" data-testid="sort-toggle">
            <button onClick={() => setSort("recent")} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 ${sort==="recent"?"bg-foreground text-bg":"text-muted"}`}><Clock size={12} /> Recent</button>
            <button onClick={() => setSort("top_week")} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 ${sort==="top_week"?"bg-foreground text-bg":"text-muted"}`}><TrendUp size={12} /> Top week</button>
          </div>
        </div>

        {/* Tag bar */}
        {tags.length > 0 && !effectiveGroup && (
          <div className="mb-5 flex gap-2 overflow-x-auto pb-2 no-scrollbar" data-testid="tag-bar">
            <button onClick={() => setTag(null)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium ${!tag?"bg-foreground text-bg":"bg-white border border-soft text-muted"}`}>All</button>
            {tags.map((t) => (
              <button key={t.tag} onClick={() => setTag(t.tag === tag ? null : t.tag)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium ${tag===t.tag?"text-bg":"bg-white border border-soft text-muted"}`} style={tag===t.tag?{background:tagColor(t.tag)}:{}} data-testid={`tag-${t.tag}`}>
                #{t.tag} · {t.count}
              </button>
            ))}
          </div>
        )}

        {/* Feed */}
        <div className="space-y-4">
          {posts.map((p) => (<PostCard key={p.id} post={p} me={me} onChange={() => load(true)} />))}
          {posts.length === 0 && (
            <div className="text-center text-sm text-muted py-12">No posts yet — be the first.</div>
          )}
        </div>

        {posts.length > 0 && hasMore && (
          <button onClick={() => load(false)} className="w-full mt-5 py-3 rounded-full bg-white border border-soft text-sm text-muted hover:border-gold">Load more</button>
        )}

        {compose !== false && (
          <ComposeModal
            seed={compose}
            groupSlug={groupSlug}
            groupName={effectiveGroup?.name}
            onClose={() => setCompose(false)}
            onPosted={() => { setCompose(false); load(true); }}
          />
        )}

        {notifsOpen && (
          <NotificationsPanel onClose={() => { setNotifsOpen(false); api.get("/forum/notifications").then(({data}) => setUnread(data.unread)); }} />
        )}
      </main>
      <Footer />
    </div>
  );
}


function GroupCoverBanner({ group, onJoin }) {
  const Icon = GROUP_ICONS[group.icon] || Sparkle;
  return (
    <div
      className="relative overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `url('/community/${group.slug}.jpg')` }}
      data-testid="group-banner"
    >
      {/* Themed tint + dark vignette so the headline reads clearly over any photo */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${group.cover_from}aa 0%, ${group.cover_to}cc 100%)`,
        }}
      />
      <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-white/15 blur-3xl" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 relative">
        <Link to="/community" className="inline-flex items-center gap-1 text-white/90 hover:text-white text-xs">
          <ArrowLeft size={12} /> All groups
        </Link>
        <div className="mt-3 flex items-start gap-4">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/95 flex items-center justify-center shadow-lg ring-1 ring-white/40 flex-none">
            <Icon size={28} weight="duotone" style={{ color: group.cover_from }} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-serif-display text-2xl sm:text-3xl text-white font-light leading-tight drop-shadow">
              {group.name}
            </h1>
            <div className="mt-1 flex items-center gap-3 text-white/90 text-xs">
              <span className="inline-flex items-center gap-1"><Users size={12} weight="bold" /> {fmtMembers(group.members)} members</span>
              <span>·</span>
              <span>{group.is_member ? "You're a member" : "Not a member yet"}</span>
            </div>
          </div>
          {!group.is_member && (
            <button
              onClick={onJoin}
              className="px-5 py-2.5 rounded-full bg-white text-stone-900 font-semibold text-sm shadow-md hover:opacity-95"
              data-testid="banner-join"
            >
              Join now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function PostCard({ post, me, onChange }) {
  const navigate = useNavigate();
  const isAdmin = me?.is_super_admin;
  const isOwn = me?.user_id === post.author_id;
  const [liked, setLiked] = useState(post.liked);
  const [lc, setLc] = useState(post.like_count);
  const [menu, setMenu] = useState(false);

  const toggleLike = async (e) => {
    e.stopPropagation();
    setLiked(!liked); setLc(lc + (liked ? -1 : 1));
    try { await api.post(`/forum/like/post/${post.id}`); } catch { setLiked(liked); setLc(lc); toast.error("Couldn't like."); }
  };

  const del = async () => {
    if (!confirm("Delete this post?")) return;
    await api.delete(`/forum/posts/${post.id}`); toast.success("Deleted"); onChange();
  };
  const pin = async () => { await api.post(`/forum/admin/pin/${post.id}`); onChange(); };
  const report = async () => {
    const reason = prompt("Why are you reporting this post?");
    if (!reason) return;
    await api.post("/forum/report", { target_kind: "post", target_id: post.id, reason });
    toast.success("Reported. Thank you.");
  };
  const ban = async () => {
    if (!confirm(`Ban ${post.name}?`)) return;
    await api.post(`/forum/admin/ban/${post.author_id}`); toast.success("Banned");
  };

  return (
    <article className="card-surface rounded-2xl p-4 sm:p-5 cursor-pointer hover:border-gold/40 transition-colors" onClick={() => navigate(`/forum/post/${post.id}`)} data-testid={`post-${post.id}`}>
      {post.is_pinned && (
        <div className="text-[10px] uppercase tracking-widest text-gold mb-2 flex items-center gap-1"><PushPin size={11} weight="fill" /> Pinned by admin</div>
      )}
      <div className="flex items-start gap-3">
        <Avatar name={post.name} picture={post.picture} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{post.name}</span>
            <span className="text-xs text-muted">· {timeAgo(post.created_at)}</span>
          </div>
          <h3 className="font-serif-display text-xl mt-1 font-light leading-snug">{post.title}</h3>
        </div>
        <button onClick={(e) => { e.stopPropagation(); setMenu(!menu); }} className="p-1 hover:bg-bg/60 rounded relative" data-testid={`menu-${post.id}`}>
          <DotsThree size={20} weight="bold" />
          {menu && (
            <div className="absolute right-0 top-7 w-44 bg-white border border-soft rounded-lg shadow-lg z-10 py-1 text-sm" onClick={(e) => e.stopPropagation()}>
              {(isOwn || isAdmin) && <button onClick={del} className="w-full px-3 py-1.5 text-left hover:bg-bg/60 text-rose-700">Delete</button>}
              {isAdmin && <button onClick={pin} className="w-full px-3 py-1.5 text-left hover:bg-bg/60">{post.is_pinned ? "Unpin" : "Pin"}</button>}
              {isAdmin && <button onClick={ban} className="w-full px-3 py-1.5 text-left hover:bg-bg/60 text-rose-700">Ban author</button>}
              {!isOwn && <button onClick={report} className="w-full px-3 py-1.5 text-left hover:bg-bg/60"><Flag size={12} className="inline mr-1" /> Report</button>}
            </div>
          )}
        </button>
      </div>
      <p className="text-sm mt-3 text-foreground/90 leading-snug whitespace-pre-line line-clamp-5">{post.body}</p>
      {/* Image: only render when approved OR when it's the author's pending preview */}
      {post.image_b64 && post.image_status !== "rejected" && (
        <div className="mt-3 relative">
          <img src={post.image_b64.startsWith("data:") ? post.image_b64 : `data:image/jpeg;base64,${post.image_b64}`} alt="" className="rounded-xl max-h-96 w-full object-cover" />
          {post.image_status === "pending" && (
            <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-amber-500/90 text-white text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1 backdrop-blur" data-testid={`pending-${post.id}`}>
              <Hourglass size={11} weight="bold" /> Pending admin approval
            </div>
          )}
        </div>
      )}
      {post.image_status === "rejected" && isOwn && (
        <div className="mt-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
          The image attached to this post wasn&apos;t approved. Your text is still live.
        </div>
      )}
      {post.tags.length > 0 && (
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {post.tags.map((t) => (
            <span key={t} className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: tagColor(t) + "33", color: tagColor(t) }}>#{t}</span>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center gap-5 text-sm text-muted">
        <button onClick={toggleLike} className={`flex items-center gap-1.5 hover:text-rose-600 ${liked?"text-rose-600":""}`} data-testid={`like-${post.id}`}>
          <Heart size={18} weight={liked?"fill":"regular"} /> {lc}
        </button>
        <span className="flex items-center gap-1.5"><ChatCircle size={18} /> {post.comment_count}</span>
      </div>
    </article>
  );
}


function Avatar({ name, picture, size = 36 }) {
  if (picture) return <img src={picture} alt="" className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />;
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return <div className="rounded-full bg-gradient-to-br from-amber-300 to-orange-500 text-white font-bold flex items-center justify-center shrink-0" style={{ width: size, height: size, fontSize: size * 0.4 }}>{initials}</div>;
}


function ComposeModal({ seed = {}, groupSlug = null, groupName = null, onClose, onPosted }) {
  const [title, setTitle] = useState(seed.title || "");
  const [body, setBody] = useState(seed.body || "");
  const [tags, setTags] = useState("");
  const [anon, setAnon] = useState(false);
  const [imageB64, setImageB64] = useState(null);
  const [busy, setBusy] = useState(false);

  const pickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Max 3 MB.");
    const fr = new FileReader();
    fr.onload = () => setImageB64(fr.result);
    fr.readAsDataURL(f);
  };

  const submit = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Title and body required.");
    setBusy(true);
    try {
      const payload = {
        title: title.trim(), body: body.trim(), image_b64: imageB64,
        tags: tags.split(/[,\s]+/).filter(Boolean), is_anonymous: anon,
      };
      if (groupSlug) payload.group_slug = groupSlug;
      await api.post("/forum/posts", payload);
      if (imageB64) {
        toast.success("Posted! Your image is queued for super-admin approval — text is already live.");
      } else {
        toast.success("Posted!");
      }
      onPosted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="compose-modal">
      <button aria-label="Close" className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:w-[560px] max-h-[92vh] rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-soft p-4 flex items-center justify-between">
          <div>
            <h2 className="font-serif-display text-xl">New post</h2>
            {groupName && <div className="text-[11px] text-muted mt-0.5">Posting to <b>{groupName}</b></div>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-bg/60"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (max 140 chars)" maxLength={140} className="w-full px-3 py-2.5 rounded-lg border border-soft text-base" data-testid="compose-title" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share your insight, question, or win…" rows={6} maxLength={5000} className="w-full px-3 py-2.5 rounded-lg border border-soft text-sm" data-testid="compose-body" />
          <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags (comma-separated, max 3)" className="w-full px-3 py-2 rounded-lg border border-soft text-sm" data-testid="compose-tags" />
          {imageB64 && (
            <>
              <div className="relative">
                <img src={imageB64} alt="" className="rounded-lg max-h-64 w-full object-cover" />
                <button onClick={() => setImageB64(null)} className="absolute top-2 right-2 p-1.5 bg-black/60 text-white rounded-full"><X size={14} /></button>
              </div>
              <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-start gap-2" data-testid="compose-image-notice">
                <ShieldCheck size={14} weight="duotone" className="flex-none mt-0.5 text-amber-700" />
                <span>Posts with images go through a brief super-admin approval (usually under an hour). Your text publishes immediately; the image appears once approved.</span>
              </div>
            </>
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Camera size={18} />
              <span>Add image</span>
              <input type="file" accept="image/*" onChange={pickFile} className="hidden" />
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} data-testid="compose-anon" />
              {anon ? <EyeSlash size={16} /> : <Eye size={16} />}
              Post anonymously
            </label>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-soft p-4">
          <button onClick={submit} disabled={busy} className="w-full btn-primary rounded-full py-2.5 text-sm font-medium disabled:opacity-60" data-testid="compose-submit">
            {busy ? "Posting…" : (groupName ? `Post to ${groupName}` : "Post to community")}
          </button>
        </div>
      </div>
    </div>
  );
}


function NotificationsPanel({ onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/forum/notifications").then(({ data }) => setItems(data.items));
    api.post("/forum/notifications/mark-read").catch(() => {});
  }, []);
  return (
    <div className="fixed inset-0 z-50" data-testid="notifications-panel">
      <button aria-label="Close" className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[400px] bg-white shadow-2xl overflow-y-auto" style={{ filter: "none" }}>
        <div className="sticky top-0 bg-white border-b border-soft p-4 flex items-center justify-between">
          <h2 className="font-serif-display text-xl">Notifications</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-bg/60"><X size={18} /></button>
        </div>
        <div className="p-3 space-y-1.5">
          {items === null && <div className="text-sm text-muted p-3">Loading…</div>}
          {items && items.length === 0 && <div className="text-sm text-muted p-3">No notifications yet.</div>}
          {items && items.map((n) => (
            <Link key={n.id} to={`/forum/post/${n.post_id}`} onClick={onClose} className={`block p-3 rounded-lg ${n.is_read?"":"bg-amber-50"} hover:bg-bg/60`}>
              <div className="text-sm"><b>{n.actor_name}</b> {n.kind === "like" ? "liked" : "commented on"} your {n.comment_id ? "comment" : "post"}.</div>
              <div className="text-xs text-muted mt-0.5">{timeAgo(n.created_at)}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}


function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}


// ─── Post detail page ───────────────────────────────────────────────────
export function ForumPostDetail() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [me, setMe] = useState(null);
  const [body, setBody] = useState("");
  const [anon, setAnon] = useState(false);

  const load = useCallback(() => {
    api.get(`/forum/posts/${id}`).then(({ data }) => setPost(data)).catch(() => toast.error("Post not found"));
    api.get(`/forum/posts/${id}/comments`).then(({ data }) => setComments(data.items));
  }, [id]);

  useEffect(() => { api.get("/auth/me").then(({data}) => setMe(data)).catch(() => {}); load(); }, [id, load]);

  if (!post) return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col"><Header /><main className="flex-1 p-8 text-center text-muted">Loading…</main><Footer /></div>
  );

  const submitComment = async () => {
    if (!body.trim()) return;
    try {
      await api.post(`/forum/posts/${id}/comments`, { body: body.trim(), is_anonymous: anon });
      setBody(""); setAnon(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't comment."); }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="forum-post-detail">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-6">
        <Link to="/forum" className="text-sm text-muted hover:text-foreground">← Community</Link>
        <div className="mt-3">
          <PostCard post={post} me={me} onChange={load} />
        </div>
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-3">Comments · {comments.length}</div>
          <div className="card-surface rounded-2xl p-4 mb-4">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add your reply…" rows={3} maxLength={2000} className="w-full px-3 py-2 rounded-lg border border-soft text-sm" data-testid="comment-input" />
            <div className="mt-2 flex items-center justify-between">
              <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} /> Anonymous</label>
              <button onClick={submitComment} className="btn-primary rounded-full px-4 py-1.5 text-sm font-medium" data-testid="comment-submit"><PaperPlaneTilt size={14} weight="fill" className="inline mr-1" /> Reply</button>
            </div>
          </div>
          <div className="space-y-2">
            {comments.map((c) => (
              <CommentRow key={c.id} comment={c} me={me} onChange={load} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}


function CommentRow({ comment, me, onChange }) {
  const [liked, setLiked] = useState(comment.liked);
  const [lc, setLc] = useState(comment.like_count);
  const isAdmin = me?.is_super_admin; const isOwn = me?.user_id === comment.author_id;
  const toggleLike = async () => {
    setLiked(!liked); setLc(lc + (liked ? -1 : 1));
    try { await api.post(`/forum/like/comment/${comment.id}`); } catch { setLiked(liked); setLc(lc); }
  };
  const del = async () => { if (!confirm("Delete?")) return; await api.delete(`/forum/comments/${comment.id}`); onChange(); };
  const report = async () => { const r = prompt("Reason?"); if (!r) return; await api.post("/forum/report", { target_kind:"comment", target_id:comment.id, reason:r }); toast.success("Reported."); };
  return (
    <div className="card-surface rounded-xl p-3 flex gap-3" data-testid={`comment-${comment.id}`}>
      <Avatar name={comment.name} picture={comment.picture} size={32} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium">{comment.name}</span>
          <span className="text-muted">· {timeAgo(comment.created_at)}</span>
        </div>
        <p className="text-sm mt-1 whitespace-pre-line">{comment.body}</p>
        <div className="mt-1.5 flex items-center gap-3 text-xs text-muted">
          <button onClick={toggleLike} className={`flex items-center gap-1 hover:text-rose-600 ${liked?"text-rose-600":""}`}>
            <Heart size={14} weight={liked?"fill":"regular"} /> {lc}
          </button>
          {(isOwn || isAdmin) && <button onClick={del} className="hover:text-rose-700">Delete</button>}
          {!isOwn && <button onClick={report} className="hover:text-foreground"><Flag size={12} className="inline" /> Report</button>}
        </div>
      </div>
    </div>
  );
}
